import { Reducer } from 'redux';

import { RequestState, DispatchedAction, SuccessAction, FailureAction } from '../../types';

const initialState = {
  lastRequested: -1,
  lastResponded: -1,
  loading: false,
  error: false,
  data: null,
  safeMessage: null,
};

const getResponseState = <D>(state: RequestState<D>, sequenceNumber: number) => ({
  lastResponded: sequenceNumber,
  loading: sequenceNumber === state.lastRequested ? false : state.loading,
});

/**
 * Returns a reducer that produces state defining the lifecycle of an HTTP request. This can be used
 * alongside `combineReducers` to store request lifecycle information in a slice of state. When a
 * request is in flight, the `loading` state is set to true, but the `data` state is not cleared.
 * The `error` state works the same way. Therefore, `data` will always hold the last data that was
 * fetched successfully.
 *
 * @generic D: The shape of the data field stored in the state and produced by the reducer.
 * @generic U: (optional) The shape of the data returned by successful requests.
 * The default is D, so if U isn't provided, the data stored in the state is of the same type returned by the request.
 * @generic R: The action type that's dispatched with the *started* event.
 * @generic S: The action type that's dispatched with the *successful* request event.
 * @generic F: The action type that's dispatched with the *failed* request event.
 * @generic B: The type that is passed as the request body. This type will also be attached to the dispatched response actions.
 *
 * @param config.mergeData (optional): A function that produces the next state of the data field,
 * given the previous state and the data returned from a successful request.
 *
 * @example
 * combineReducers({
 *  foo: createRequestReducer<number>()('FOO_REQUEST', 'FOO_SUCCESS', 'FOO_FAILURE'),
 *  bar: createRequestReducer<number>()('BAR_REQUEST', 'BAR_SUCCESS', 'BAR_FAILURE'),
 * })
 *
 * @example With the mergeData function provided
 * createRequestReducer<Resource[], Resource>({
 *   mergeData: (resources, resource) => [...resources, resource],
 * })(
 *   'RESOURCE_REQUEST',
 *   'RESOURCE_SUCCESS',
 *   'RESOURCE_FAILURE'
 * );
 */
export function createRequestReducer<D>(): <
  R extends string,
  S extends string,
  F extends string,
  B,
>(
  requestType: R,
  successType: S,
  failureType: F,
) => Reducer<RequestState<D>, DispatchedAction<R, S, F, B, D>>;

export function createRequestReducer<D, U>(config: {
  mergeData: (data: D, update: U) => D;
}): <R extends string, S extends string, F extends string, B>(
  requestType: R,
  successType: S,
  failureType: F,
) => Reducer<RequestState<D>, DispatchedAction<R, S, F, B, U>>;

export function createRequestReducer<D, U = D>(
  config: {
    mergeData?: (data: D | null, update: U) => D;
  } = {},
) {
  const configWithDefaults = {
    mergeData: (_: D | null, update: U) => update as unknown as D,
    ...config,
  };

  return <R extends string, S extends string, F extends string, B>(
      requestType: R,
      successType: S,
      failureType: F,
    ) =>
    (state = initialState, action: DispatchedAction<R, S, F, B, U>) => {
      const { sequenceNumber } = action;

      switch (action.type) {
        case requestType:
          return { ...state, lastRequested: sequenceNumber, loading: true };
        case successType: {
          const { data: update } = action as SuccessAction<S, B, U>;
          const data = configWithDefaults.mergeData(state.data, update);

          return sequenceNumber === state.lastRequested
            ? {
                ...state,
                ...getResponseState(state, sequenceNumber),
                error: false,
                data,
                safeMessage: null,
              }
            : state;
        }
        case failureType: {
          const { error } = action as FailureAction<F, B>;
          return sequenceNumber === state.lastRequested
            ? {
                ...state,
                ...getResponseState(state, sequenceNumber),
                error: true,
                safeMessage: error?.safeMessage ?? null,
              }
            : state;
        }
      }

      return state;
    };
}

export const createLoadingReducer =
  <R extends string, S extends string, F extends string>(
    requestType: R,
    successType: S,
    failureType: F,
  ): Reducer<boolean> =>
  (state = false, action) => {
    switch (action.type) {
      case requestType:
        return true;
      case successType:
        return false;
      case failureType:
        return false;
    }
    return state;
  };

export const createErrorReducer =
  <R extends string, S extends string, F extends string>(
    requestType: R,
    successType: S,
    failureType: F,
  ): Reducer<boolean> =>
  (state = false, action) => {
    switch (action.type) {
      case requestType:
        return state;
      case successType:
        return false;
      case failureType:
        return true;
    }
    return state;
  };
